import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Link,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../contexts/ApiContext';
import {
  formatFlightDisplay,
  formatFlightStatusDisplay,
  flightStatusChipColor,
  type BookingFlightDetails,
} from '../utils/bookingFlightDetails';
import PropertyNicknameChip, { formatPropertyNickname } from '../components/PropertyNicknameChip';
import { airbnbConfirmationCodeFromBooking } from '../utils/airbnbConfirmationCode';
import { isExportSource } from '../utils/bookingSources';
import BookingCommsPanel from '../components/BookingCommsPanel';

interface BookingHistoryEntry {
  date_fetched: string;
  data: Record<string, unknown>;
}

interface LinkedPairDifference {
  field: string;
  ical: unknown;
  manual: unknown;
  differs: boolean;
}

interface LinkedPairBookingSide {
  id: number;
  source?: string;
  reservation_url?: string | null;
  guest_name?: string | null;
  adults_count?: number | null;
  children_count?: number | null;
  [key: string]: unknown;
}

interface LinkedPairPayload {
  link_id: number;
  status?: string;
  confirmed_by_user?: boolean;
  confidence_score?: number | null;
  confirmation_code_match?: boolean;
  confirmation_code?: string | null;
  ical_booking_id: number;
  manual_booking_id: number;
  ical_booking?: LinkedPairBookingSide | null;
  manual_booking?: LinkedPairBookingSide | null;
  differences?: LinkedPairDifference[];
  preferred_source_note?: string;
}

interface BookingDetailModel {
  id: number;
  property_id: number;
  property_nickname?: string;
  platform: string;
  source: string;
  external_booking_id?: string | null;
  guest_name?: string | null;
  guest_email?: string | null;
  guest_phone_last_4?: string | null;
  reservation_url?: string | null;
  check_in_date: string;
  check_out_date: string;
  nights: number;
  adults_count?: number | null;
  children_count?: number | null;
  total_payout_amount?: number | null;
  host_payout_amount?: number | null;
  currency: string;
  status: string;
  booking_type: string;
  summary?: string | null;
  description?: string | null;
  notes?: string | null;
  last_sync?: string | null;
  booked_at?: string | null;
  booked_at_fallback?: string | null;
  booked_at_display?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  booking_history?: BookingHistoryEntry[];
  flight_in?: BookingFlightDetails | null;
  flight_out?: BookingFlightDetails | null;
  flight_in_display?: string;
  flight_out_display?: string;
  flight_in_status_display?: string;
  flight_out_status_display?: string;
  linked_pair?: LinkedPairPayload | null;
}

function formatLinkedFieldLabel(field: string): string {
  return field
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatLinkedFieldValue(value: unknown): string {
  if (value == null || value === '') return '—';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return String(value);
}

function getUpdatedFields(history?: BookingHistoryEntry[]): Set<string> {
  const changed = new Set<string>();
  if (!history || history.length < 2) return changed;
  const prev = history[history.length - 2]?.data || {};
  const curr = history[history.length - 1]?.data || {};
  const keys = Array.from(new Set([...Object.keys(prev), ...Object.keys(curr)]));
  const normalize = (v: unknown): unknown => {
    if (v == null) return null;
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
    return v;
  };
  for (const k of keys) {
    if (k === 'booked_at' || k === 'booked_at_fallback') continue;
    if (normalize(prev[k]) !== normalize(curr[k])) changed.add(k);
  }
  return changed;
}

function getMeaningfulUpdatesCount(history?: BookingHistoryEntry[]): number {
  if (!history || history.length < 2) return 0;
  let count = 0;
  for (let i = 1; i < history.length; i += 1) {
    const prev = history[i - 1]?.data || {};
    const curr = history[i]?.data || {};
    const keys = Array.from(new Set([...Object.keys(prev), ...Object.keys(curr)]));
    const changed = keys.some((k) => {
      if (k === 'booked_at' || k === 'booked_at_fallback') return false;
      const p = prev[k] == null ? null : prev[k];
      const c = curr[k] == null ? null : curr[k];
      return p !== c;
    });
    if (changed) count += 1;
  }
  return count;
}

function extractAirbnbConfirmationCode(booking: BookingDetailModel | null): string {
  if (!booking) return '';
  return airbnbConfirmationCodeFromBooking(booking);
}

const TAB_NAMES = ['details', 'comms', 'metadata'] as const;
type BookingDetailTab = (typeof TAB_NAMES)[number];

function tabNameToIndex(name: string | null): number {
  const raw = (name || '').toLowerCase();
  const normalized = raw === 'metdata' ? 'metadata' : raw;
  const i = TAB_NAMES.indexOf(normalized as BookingDetailTab);
  return i >= 0 ? i : 0;
}

function a11yProps(index: number) {
  return {
    id: `booking-detail-tab-${index}`,
    'aria-controls': `booking-detail-tabpanel-${index}`,
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`booking-detail-tabpanel-${index}`}
      aria-labelledby={`booking-detail-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

function DetailsSection({
  title,
  children,
  defaultExpanded = true,
}: {
  title: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
}) {
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      sx={{
        mb: 1,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        '&:before': { display: 'none' },
        '&.Mui-expanded': { margin: 0, mb: 1 },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1" fontWeight={600}>
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  );
}

const BookingDetail: React.FC = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  const [booking, setBooking] = useState<BookingDetailModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(() => tabNameToIndex(searchParams.get('tab')));

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return dateString;
    // Prefer calendar date part for date-only strings to avoid TZ day shifts.
    const m = String(dateString).match(/^(\d{4})-(\d{2})-(\d{2})/);
    const date = m
      ? new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
      : d;
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: m ? 'UTC' : undefined,
    });
  };
  const formatDateTime = (dateString: string) => {
    if (!dateString) return 'N/A';
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return dateString;
    return d.toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };
  const formatAmount = (amount: number | null | undefined, currency: string = 'EUR') => {
    if (amount == null) return 'No amount';
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
  };

  const loadBooking = useCallback(async () => {
    const id = Number.parseInt(bookingId || '', 10);
    if (!Number.isFinite(id)) {
      setError('Invalid booking id');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/api/calendar/unified/bookings/${id}`);
      const b = res.data?.booking as BookingDetailModel;
      if (!b) {
        setError('Booking not found');
        setBooking(null);
        return;
      }
      setBooking(b);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load booking');
      setBooking(null);
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    void loadBooking();
  }, [loadBooking]);

  useEffect(() => {
    const next = tabNameToIndex(searchParams.get('tab'));
    if (next !== tabValue) setTabValue(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    const next = new URLSearchParams(searchParams);
    next.set('tab', TAB_NAMES[newValue] || 'details');
    setSearchParams(next, { replace: true });
  };

  if (!user || (user.role !== 'admin' && !user.can_view_calendar)) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ my: 4 }}>
          <Alert severity="warning">You don't have permission to view bookings.</Alert>
        </Box>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ my: 6, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !booking) {
    return (
      <Container maxWidth="md">
        <Box sx={{ my: 4 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/bookings')} sx={{ mb: 2 }}>
            Back to bookings
          </Button>
          <Alert severity="error">{error || 'Booking not found'}</Alert>
        </Box>
      </Container>
    );
  }

  const updatedFields = getUpdatedFields(booking.booking_history);
  const UpdatedBadge = ({ field }: { field: string }) =>
    updatedFields.has(field) ? (
      <Chip label="Updated" size="small" color="warning" sx={{ ml: 0.5, height: 18, fontSize: '0.7rem' }} />
    ) : null;

  return (
    <Container maxWidth="md">
      <Box sx={{ my: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          component={RouterLink}
          to="/bookings"
          sx={{ mb: 2 }}
        >
          Back to bookings
        </Button>

        <Typography variant="h4" component="h1" gutterBottom>
          Booking #{booking.id}
          {extractAirbnbConfirmationCode(booking) ? ` · ${extractAirbnbConfirmationCode(booking)}` : ''}
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <PropertyNicknameChip
            nickname={booking.property_nickname}
            propertyId={booking.property_id}
          />
          <span>
            {formatDate(booking.check_in_date)} – {formatDate(booking.check_out_date)}
          </span>
        </Typography>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="booking detail tabs">
            <Tab label="Details" {...a11yProps(0)} />
            <Tab label="Comms" {...a11yProps(1)} />
            <Tab label="Metadata" {...a11yProps(2)} />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <DetailsSection title="Basic Information">
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">Booking ID</Typography>
                <Typography variant="body1" fontWeight="medium">{booking.id}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Property</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <PropertyNicknameChip
                    nickname={booking.property_nickname}
                    propertyId={booking.property_id}
                  />
                  {!formatPropertyNickname(booking.property_nickname, booking.property_id) && (
                    <Typography variant="body1">—</Typography>
                  )}
                </Box>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Platform <UpdatedBadge field="platform" />
                </Typography>
                <Chip
                  label={booking.platform}
                  size="small"
                  color={booking.platform === 'Airbnb' ? 'primary' : 'secondary'}
                />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Source <UpdatedBadge field="source" />
                </Typography>
                <Chip
                  label={booking.source}
                  size="small"
                  color={
                    booking.source === 'iCal'
                      ? 'info'
                      : isExportSource(booking.source)
                        ? 'warning'
                        : 'default'
                  }
                />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Status <UpdatedBadge field="status" />
                </Typography>
                <Chip
                  label={booking.status}
                  size="small"
                  color={
                    booking.status === 'confirmed'
                      ? 'success'
                      : booking.status === 'cancelled'
                        ? 'error'
                        : 'warning'
                  }
                />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Booking Type <UpdatedBadge field="booking_type" />
                </Typography>
                <Typography variant="body1">{booking.booking_type}</Typography>
              </Box>
              {booking.external_booking_id && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    External Booking ID <UpdatedBadge field="external_booking_id" />
                  </Typography>
                  <Typography variant="body1">{booking.external_booking_id}</Typography>
                </Box>
              )}
            </Box>
          </DetailsSection>

          <DetailsSection title="Guest Information">
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Guest Name <UpdatedBadge field="guest_name" />
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {booking.guest_name || 'N/A'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Guest Email <UpdatedBadge field="guest_email" />
                </Typography>
                <Typography variant="body1">{booking.guest_email || 'N/A'}</Typography>
              </Box>
              {booking.guest_phone_last_4 && (
                <Box>
                  <Typography variant="body2" color="text.secondary">Guest Phone (Last 4)</Typography>
                  <Typography variant="body1">{booking.guest_phone_last_4}</Typography>
                </Box>
              )}
              {booking.reservation_url && (
                <Box>
                  <Typography variant="body2" color="text.secondary">Reservation URL</Typography>
                  <Typography
                    variant="body2"
                    component="a"
                    href={booking.reservation_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: 'primary.main', textDecoration: 'underline', display: 'block' }}
                  >
                    {booking.reservation_url}
                  </Typography>
                </Box>
              )}
            </Box>
          </DetailsSection>

          <DetailsSection title="Dates">
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Check-in <UpdatedBadge field="check_in_date" />
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {formatDate(booking.check_in_date)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Check-out <UpdatedBadge field="check_out_date" />
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {formatDate(booking.check_out_date)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Nights <UpdatedBadge field="nights" />
                </Typography>
                <Typography variant="body1" fontWeight="medium">{booking.nights}</Typography>
              </Box>
            </Box>
          </DetailsSection>

          <DetailsSection title="Flights">
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Flight IN
                </Typography>
                <Typography variant="body1">
                  {formatFlightDisplay(booking.flight_in, booking.flight_in_display)}
                </Typography>
                {formatFlightStatusDisplay(booking.flight_in, booking.flight_in_status_display) ? (
                  <Chip
                    size="small"
                    label={formatFlightStatusDisplay(booking.flight_in, booking.flight_in_status_display) || ''}
                    color={flightStatusChipColor(
                      formatFlightStatusDisplay(booking.flight_in, booking.flight_in_status_display)
                    )}
                    sx={{ mt: 1 }}
                  />
                ) : null}
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Flight OUT
                </Typography>
                <Typography variant="body1">
                  {formatFlightDisplay(booking.flight_out, booking.flight_out_display)}
                </Typography>
                {formatFlightStatusDisplay(booking.flight_out, booking.flight_out_status_display) ? (
                  <Chip
                    size="small"
                    label={formatFlightStatusDisplay(booking.flight_out, booking.flight_out_status_display) || ''}
                    color={flightStatusChipColor(
                      formatFlightStatusDisplay(booking.flight_out, booking.flight_out_status_display)
                    )}
                    sx={{ mt: 1 }}
                  />
                ) : null}
              </Box>
            </Box>
          </DetailsSection>

          <DetailsSection title="Guest Count">
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Adults <UpdatedBadge field="adults_count" />
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {booking.adults_count != null ? booking.adults_count : '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Children <UpdatedBadge field="children_count" />
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {booking.children_count != null ? booking.children_count : '—'}
                </Typography>
              </Box>
            </Box>
          </DetailsSection>

          <DetailsSection title="Financial Information">
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Currency <UpdatedBadge field="currency" />
                </Typography>
                <Typography variant="body1" fontWeight="medium">{booking.currency}</Typography>
              </Box>
              {booking.total_payout_amount != null && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Payout <UpdatedBadge field="total_payout_amount" />
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {formatAmount(booking.total_payout_amount, booking.currency)}
                  </Typography>
                </Box>
              )}
              {booking.host_payout_amount != null && (
                <Box>
                  <Typography variant="body2" color="text.secondary">Host Payout</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {formatAmount(booking.host_payout_amount, booking.currency)}
                  </Typography>
                </Box>
              )}
            </Box>
          </DetailsSection>

          {(booking.summary || booking.description || booking.notes) && (
            <DetailsSection title="Additional Information">
              {booking.summary && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Summary <UpdatedBadge field="summary" />
                  </Typography>
                  <Typography variant="body1">{booking.summary}</Typography>
                </Box>
              )}
              {booking.description && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Description
                  </Typography>
                  <Typography variant="body1">{booking.description}</Typography>
                </Box>
              )}
              {booking.notes && (
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Notes <UpdatedBadge field="notes" />
                  </Typography>
                  <Typography variant="body1">{booking.notes}</Typography>
                </Box>
              )}
            </DetailsSection>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <BookingCommsPanel booking={booking} confirmationCode={extractAirbnbConfirmationCode(booking)} />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            Metadata
          </Typography>

          {booking.linked_pair && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Linked booking pair
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {booking.linked_pair.preferred_source_note ||
                  'Manual / AirBnB Export is usually authoritative for guest counts; iCal often lags.'}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, alignItems: 'center' }}>
                <Chip
                  size="small"
                  color="success"
                  label={
                    booking.linked_pair.confirmation_code_match
                      ? 'Confirmation-code match'
                      : booking.linked_pair.confirmed_by_user
                        ? 'User-confirmed link'
                        : 'Confirmed link'
                  }
                />
                {booking.linked_pair.confirmation_code ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={booking.linked_pair.confirmation_code}
                    component="a"
                    href={
                      booking.linked_pair.manual_booking?.reservation_url ||
                      booking.linked_pair.ical_booking?.reservation_url ||
                      `https://www.airbnb.com/hosting/reservations/details/${booking.linked_pair.confirmation_code}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    clickable
                    sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}
                  />
                ) : null}
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Manual / Export row</Typography>
                  <Link
                    component={RouterLink}
                    to={`/bookings/${booking.linked_pair.manual_booking_id}?tab=metadata`}
                    underline="hover"
                    sx={{ fontWeight: 600 }}
                  >
                    Booking #{booking.linked_pair.manual_booking_id}
                  </Link>
                  {booking.linked_pair.manual_booking?.reservation_url ? (
                    <Typography
                      variant="body2"
                      component="a"
                      href={String(booking.linked_pair.manual_booking.reservation_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ display: 'block', mt: 0.5, color: 'primary.main', textDecoration: 'underline', wordBreak: 'break-all' }}
                    >
                      {String(booking.linked_pair.manual_booking.reservation_url)}
                    </Typography>
                  ) : null}
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">iCal row</Typography>
                  <Link
                    component={RouterLink}
                    to={`/bookings/${booking.linked_pair.ical_booking_id}?tab=metadata`}
                    underline="hover"
                    sx={{ fontWeight: 600 }}
                  >
                    Booking #{booking.linked_pair.ical_booking_id}
                  </Link>
                  {booking.linked_pair.ical_booking?.reservation_url ? (
                    <Typography
                      variant="body2"
                      component="a"
                      href={String(booking.linked_pair.ical_booking.reservation_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ display: 'block', mt: 0.5, color: 'primary.main', textDecoration: 'underline', wordBreak: 'break-all' }}
                    >
                      {String(booking.linked_pair.ical_booking.reservation_url)}
                    </Typography>
                  ) : null}
                </Box>
              </Box>

              {(() => {
                const diffs = (booking.linked_pair.differences || []).filter((d) => d.differs);
                if (!diffs.length) {
                  return (
                    <Alert severity="info" sx={{ mb: 1 }}>
                      No field differences between the iCal and Manual/Export rows.
                    </Alert>
                  );
                }
                return (
                  <>
                    <Typography variant="subtitle2" gutterBottom>
                      Differences ({diffs.length}) — Manual/Export values are usually correct for guests
                    </Typography>
                    <Table size="small" sx={{ mb: 1 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Field</TableCell>
                          <TableCell>iCal</TableCell>
                          <TableCell>Manual / Export</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {diffs.map((d) => (
                          <TableRow key={d.field} sx={{ bgcolor: 'action.hover' }}>
                            <TableCell sx={{ fontWeight: 600 }}>{formatLinkedFieldLabel(d.field)}</TableCell>
                            <TableCell
                              sx={
                                d.field === 'reservation_url' && d.ical
                                  ? undefined
                                  : { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }
                              }
                            >
                              {d.field === 'reservation_url' && d.ical ? (
                                <Link href={String(d.ical)} target="_blank" rel="noopener noreferrer">
                                  {String(d.ical)}
                                </Link>
                              ) : (
                                formatLinkedFieldValue(d.ical)
                              )}
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                ...(d.field === 'reservation_url' && d.manual
                                  ? {}
                                  : { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }),
                              }}
                            >
                              {d.field === 'reservation_url' && d.manual ? (
                                <Link href={String(d.manual)} target="_blank" rel="noopener noreferrer">
                                  {String(d.manual)}
                                </Link>
                              ) : (
                                formatLinkedFieldValue(d.manual)
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </>
                );
              })()}
              <Divider sx={{ my: 3 }} />
            </Box>
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 3 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">Updates</Typography>
              <Typography variant="body1">{getMeaningfulUpdatesCount(booking.booking_history)}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Booked <UpdatedBadge field="booked_at" />
              </Typography>
              <Typography variant="body1">
                {(booking.booked_at_display || booking.booked_at_fallback || booking.booked_at)
                  ? formatDateTime(
                      booking.booked_at_display || booking.booked_at_fallback || booking.booked_at || ''
                    )
                  : '—'}
              </Typography>
            </Box>
            {booking.created_at && (
              <Box>
                <Typography variant="body2" color="text.secondary">First Seen</Typography>
                <Typography variant="body1">{formatDateTime(booking.created_at)}</Typography>
              </Box>
            )}
            {booking.updated_at && (
              <Box>
                <Typography variant="body2" color="text.secondary">Updated At</Typography>
                <Typography variant="body1">{formatDateTime(booking.updated_at)}</Typography>
              </Box>
            )}
            {booking.last_sync && (
              <Box>
                <Typography variant="body2" color="text.secondary">Last Sync</Typography>
                <Typography variant="body1">{formatDateTime(booking.last_sync)}</Typography>
              </Box>
            )}
          </Box>

          {booking.booking_history && booking.booking_history.length > 0 && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Fetch History
              </Typography>
              {booking.booking_history.map((entry, idx) => (
                <Accordion key={idx} sx={{ mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="body2" fontWeight="medium">
                      {formatDateTime(entry.date_fetched)}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <pre style={{ margin: 0, fontSize: '12px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {JSON.stringify(entry.data, null, 2)}
                      </pre>
                    </Paper>
                  </AccordionDetails>
                </Accordion>
              ))}
            </>
          )}

          <Divider sx={{ my: 3 }} />
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" fontWeight="medium">
                Raw JSON Data
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                <pre style={{ margin: 0, fontSize: '12px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {JSON.stringify(booking, null, 2)}
                </pre>
              </Paper>
            </AccordionDetails>
          </Accordion>
        </TabPanel>
      </Box>
    </Container>
  );
};

export default BookingDetail;
